import * as React from 'react'
import { StepTypeNames, ELEMENT_BASE_PARAMS, FlowStep, Permissions, ElementOptions, Divider, ELEMENT_TYPES, ElementValue, NewModal, NotificationType, NewLoading as Loading, NewButton } from 'prace-common-components'
import Grid from '@material-ui/core/Grid'
import { MinMaxConfig } from './MinMaxConfig'
import { ElementPermission } from 'components/ElementPermission'
import { $ButtonContainer, $DeleteButton, $StepTypeLabel, $StepTypeSpan } from './styles'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useDeleteFlowElementsMutation, useUpdateElementMutation } from 'store/api/calls/elements'
import { StepTypeModal } from 'components/StepTypeModal'
import { DeleteElementModal } from 'components/DeleteElementModal'
import { RootState } from 'store'
import { StepTypeEntry } from 'components/StepTypeModal/types'

export const EditStep: React.FC<{step: FlowStep}> = ({ step }) => {
  const dispatch = useAppDispatch()
  const [updateElement] = useUpdateElementMutation()
  const [deleteElement, { isLoading: isDeleting }] = useDeleteFlowElementsMutation()
  const { config, permissions = [], id, order } = step
  const call = useAppSelector((state: RootState) => state.calls.call)
  const allSteps = call.flow
  const takenStepTypes = allSteps
    .filter((s) => s.id !== id)
    .flatMap((s) => ((s.config as any)?.stepTypeConfigs ?? []).map((stc: any) => stc.stepType))

  const [stepTypeModalOpen, setStepTypeModalOpen] = React.useState(false)
  const [isSavingStepType, setIsSavingStepType] = React.useState(false)
  const [openDelete, setOpenDelete] = React.useState(false)

  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout>>()
  React.useEffect(() => () => clearTimeout(debounceTimerRef.current), [id])

  const onChangeOption = (name: string, value: ElementValue) => {
    clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const newValues = ELEMENT_BASE_PARAMS.includes(name)
          ? { id: step.id, [name]: value }
          : { id: step.id, config: { ...step.config, [name]: value }}
        await updateElement(newValues).unwrap()
        dispatch({ type: 'updateStep', payload: { newValues } })
      } catch (err) {
        console.log(err)
      }
    }, 1000)
  }

  const onChangeElementPermission = async (name: string, value: ElementValue) => {
    try {
      const newValues = {id: step.id, permissions: {...permissions, [name]: value } as Permissions}
      await updateElement(newValues).unwrap()
      dispatch({ type: 'updateStep', payload: { newValues } })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Permissions updated' } })
    } catch (err) {
      console.log(err)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteElement({ ids: [id] }).unwrap()
      dispatch({ type: 'deleteStep', payload: { stepId: id } })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Step deleted' } })
    } catch (err) {
      console.log(err)
    }
  }

  const handleSaveStepType = async (stepTypeConfigs: StepTypeEntry[]) => {
    try {
      setIsSavingStepType(true)
      const newConfig: any = { ...step.config, stepTypeConfigs }
      const newValues = { id: step.id, config: newConfig }
      await updateElement(newValues).unwrap()
      dispatch({ type: 'updateStep', payload: { newValues } })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Step type saved' } })
      setStepTypeModalOpen(false)
    } catch (err) {
      console.log(err)
      dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'Failed to save step type' } })
    } finally {
      setIsSavingStepType(false)
    }
  }

  const stepTypeConfigs = (config as any)?.stepTypeConfigs ?? []

  return (
    <Grid container>
      <Loading loading={isDeleting}/>
      {stepTypeConfigs.length > 0 && (
        <Grid item xs={12}>
          <$StepTypeLabel>
            Step Types:{' '}
            <$StepTypeSpan>
              {stepTypeConfigs.map((stc: any) => StepTypeNames[stc.stepType as keyof typeof StepTypeNames]).join(', ')}
            </$StepTypeSpan>
          </$StepTypeLabel>
        </Grid>
      )}
      <$ButtonContainer item xs={12}>
        <NewButton variant='outlined' onClick={() => setStepTypeModalOpen(true)}>Configure Step Type</NewButton>
      </$ButtonContainer>
      <ElementOptions element={step} onChange={onChangeOption} />
      <Divider />
      <ElementPermission
        permissions={permissions as Permissions}
        type={ELEMENT_TYPES.STEP}
        step={order}
        id={id}
        onChange={onChangeElementPermission}
      />
      <Divider />
      <MinMaxConfig
        title='Accepted Assignments'
        minName='minAssignments'
        maxName='maxAssignments'
        min={config?.minAssignments || null}
        max={config?.maxAssignments || null}
        onChange={onChangeOption}
      />
      <Divider />
      <$DeleteButton error onClick={() => setOpenDelete(true)}>Delete Step</$DeleteButton>
      {openDelete &&
        <NewModal noTopMargin open title={`Delete ${step.title}`} onClose={() => setOpenDelete(false)}>
          <DeleteElementModal
            call={call}
            elements={[step]}
            description='Deleting this step will also delete all forms and elements inside.'
            handleDelete={() => {
              setOpenDelete(false)
              return handleDelete()
            }}
            handleCancel={() => setOpenDelete(false)}
          />
        </NewModal>
      }
      {stepTypeModalOpen && (
        <StepTypeModal
          step={step}
          open={stepTypeModalOpen}
          onClose={() => setStepTypeModalOpen(false)}
          onSave={handleSaveStepType}
          loading={isSavingStepType}
          takenStepTypes={takenStepTypes}
        />
      )}
    </Grid>
  )
}
